import React, { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@lognai/synapse-design-system-v2';
import type { AiBundle, AiRiskBand, AiBundleStatus } from '@lognai/plugin-ai-core/src/types/bundle';
import type { AiDraftItem } from '@lognai/plugin-ai-core/src/types/draft-item';
import { useAiBundle } from '../../hooks/use-bundle';
import { WorkflowProgressCard } from '../WorkflowProgressCard';
import { deriveStepsFromDrafts } from '../../hooks/use-workflow-run';
import { useApplyAiBundle, useCancelAiBundle } from '../../hooks/use-bundle-mutations';
import { draftPresentationFor, type DraftPresentation } from '../../utils/draft-presentation';
import { entityRouteFor } from '../../utils/entity-route-for';

type BadgeTone = 'neutral' | 'success' | 'warning' | 'destructive' | 'info';

export interface DraftBundleCardProps {
  bundleId: string;
  onReview?: (bundleId: string) => void;
  onViewHistory?: (bundleId: string) => void;
}

const RISK_TONE: Record<AiRiskBand, BadgeTone> = {
  low: 'info',
  medium: 'warning',
  high: 'destructive',
};

const RISK_LABEL: Record<AiRiskBand, string> = {
  low: 'Low risk',
  medium: 'Medium risk',
  high: 'High risk',
};

const STATUS_LABEL: Record<AiBundleStatus, string> = {
  pending: 'Awaiting review',
  applied: 'Applied',
  partially_applied: 'Partially applied',
  partially_applied_with_errors: 'Partially applied (with errors)',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

const isFinal = (status: AiBundleStatus): boolean =>
  status === 'applied' || status === 'partially_applied' || status === 'partially_applied_with_errors' || status === 'rejected' || status === 'cancelled';

// A bundle is "paused" when at least one step failed mid-run and the user must
// resolve it before the rest can proceed.
const isPaused = (status: AiBundleStatus): boolean =>
  status === 'partially_applied_with_errors';

// Order the bundle's items into step order. `draftIndex` is the canonical
// per-bundle ordinal (1-based, set at create time); fall back to createdAt.
const sortItems = (items: AiDraftItem[]): AiDraftItem[] =>
  [...items].sort((a, b) => {
    const ai = a.draftIndex ?? Number.MAX_SAFE_INTEGER;
    const bi = b.draftIndex ?? Number.MAX_SAFE_INTEGER;
    if (ai !== bi) return ai - bi;
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });

/**
 * One change of a kind its plugin presented (see draft-presentation): the
 * plugin's own words and a single action, instead of Review → Apply selected.
 */
const PresentedDraftCard: React.FC<{
  bundle: AiBundle;
  item: AiDraftItem;
  presentation: DraftPresentation;
  onReview?: (bundleId: string) => void;
}> = ({ bundle, item, presentation, onReview }) => {
  const apply = useApplyAiBundle();
  const discard = useCancelAiBundle();
  const [showDetails, setShowDetails] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const Preview = presentation.Preview;
  const details = presentation.detailsOf?.(item.payload) ?? [];
  const applied = item.status === 'applied';
  // Dropped by the user, or replaced when they corrected it in chat.
  const dropped =
    bundle.status === 'cancelled' || bundle.status === 'rejected' || item.status === 'rejected';
  const href = applied
    ? entityRouteFor({
        pluginId: item.pluginId,
        entityType: item.entityType,
        entityId: item.entityId ?? item.createdEntityId,
      })
    : null;
  const failed = apply.error?.message ?? discard.error?.message ?? item.failureMessage;

  if (dropped) {
    return (
      <Card style={{ marginTop: 8, opacity: 0.7 }}>
        <CardContent>
          <div style={{ fontSize: 13, fontWeight: 600, textDecoration: 'line-through' }}>{item.uiLabel}</div>
          <div style={{ fontSize: 13, color: 'var(--synapse-text-muted, #6b7280)', marginTop: 4 }}>
            {item.rejectedReason === 'Replaced by a newer proposal'
              ? 'Replaced by the newer proposal below.'
              : 'Discarded. Nothing was changed.'}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card style={{ marginTop: 8 }}>
      <CardContent>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>
            {applied ? presentation.appliedTitle : item.uiLabel}
          </div>
          {applied
            ? presentation.appliedNote && (
                <div style={{ fontSize: 13, color: 'var(--synapse-text-muted, #6b7280)' }}>
                  {presentation.appliedNote}
                </div>
              )
            : item.uiSummary && (
                <div style={{ fontSize: 13, color: 'var(--synapse-text-muted, #6b7280)' }}>
                  {item.uiSummary}
                </div>
              )}
          {Preview && previewOpen && (
            <Dialog open onOpenChange={(open: boolean) => !open && setPreviewOpen(false)}>
              <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
                <DialogHeader>
                  <DialogTitle>{item.uiLabel}</DialogTitle>
                </DialogHeader>
                <Preview payload={item.payload} />
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setPreviewOpen(false)}>
                    Close
                  </Button>
                  {bundle.status === 'pending' && (
                    <Button
                      variant="primary"
                      isLoading={apply.isPending}
                      onClick={() => {
                        apply.mutate(bundle.id);
                        setPreviewOpen(false);
                      }}
                    >
                      {presentation.applyLabel}
                    </Button>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {showDetails && details.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {details.map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </ul>
          )}
          {failed && (
            <div role="alert" style={{ fontSize: 13, color: 'var(--synapse-destructive, #b91c1c)' }}>
              {failed}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {bundle.status === 'pending' && (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  isLoading={apply.isPending}
                  onClick={() => apply.mutate(bundle.id)}
                >
                  {presentation.applyLabel}
                </Button>
                {Preview && (
                  <Button variant="secondary" size="sm" onClick={() => setPreviewOpen(true)}>
                    Preview
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  isLoading={discard.isPending}
                  onClick={() => discard.mutate({ bundleId: bundle.id, reason: 'Discarded in chat' })}
                >
                  Discard
                </Button>
                {/* A proposal made before its plugin wrote sentences has none: no Details then, rather than an empty list. */}
                {!Preview && (!presentation.detailsOf || details.length > 0) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-expanded={presentation.detailsOf ? showDetails : undefined}
                    onClick={() =>
                      presentation.detailsOf ? setShowDetails((open) => !open) : onReview?.(bundle.id)
                    }
                  >
                    {showDetails ? 'Hide details' : 'Details'}
                  </Button>
                )}
              </>
            )}
            {href && presentation.openLabel && (
              <Button variant="primary" size="sm" onClick={() => window.location.assign(href)}>
                {presentation.openLabel}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const DraftBundleCard: React.FC<DraftBundleCardProps> = ({ bundleId, onReview, onViewHistory }) => {
  const { data, isPending, isError } = useAiBundle(bundleId);

  if (isPending) {
    return (
      <Card style={{ marginTop: 8 }}>
        <CardContent>
          <div style={{ fontSize: 13, color: 'var(--synapse-text-muted, #6b7280)' }}>Loading…</div>
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card style={{ marginTop: 8 }}>
        <CardContent>
          <div style={{ fontSize: 13, color: 'var(--synapse-text-muted, #6b7280)' }}>Not available.</div>
        </CardContent>
      </Card>
    );
  }

  const bundle: AiBundle = data.bundle;
  const items = data.items ?? [];
  // A turn opens its bundle before the first draft is stored; when that draft is
  // refused (another one is still waiting on the same record) the bundle stays
  // empty. There is nothing to review, so show nothing rather than "1 change".
  if (items.length === 0) return null;
  const final = isFinal(bundle.status);
  const paused = isPaused(bundle.status);

  // Action affordance: pending → Review; paused (errors) → Resolve; otherwise
  // (applied / partial / rejected / cancelled) → View history.
  const actionButton = paused ? (
    <Button variant="primary" size="sm" onClick={() => onReview?.(bundleId)}>Resolve</Button>
  ) : final ? (
    <Button variant="ghost" size="sm" onClick={() => onViewHistory?.(bundleId)}>View history</Button>
  ) : (
    <Button variant="primary" size="sm" onClick={() => onReview?.(bundleId)}>Review</Button>
  );

  // ── Multi-step (chained) bundle → render a numbered step list. We derive the
  // steps from the bundle's own items (no workflowRunId needed), so the chat
  // reads as "a sequence of steps", not "N unrelated drafts".
  if (items.length > 1) {
    const ordered = sortItems(items);
    const { steps } = deriveStepsFromDrafts(ordered);
    const firstFailed = steps.find((s) => s.status === 'failed');
    const headline = paused && firstFailed
      ? `Paused — step ${firstFailed.index + 1} failed`
      : bundle.status === 'pending'
      ? 'Awaiting your review'
      : STATUS_LABEL[bundle.status];

    return (
      <Card style={{ marginTop: 8 }}>
        <CardContent>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <Badge tone={RISK_TONE[bundle.riskBandMax]}>{RISK_LABEL[bundle.riskBandMax]}</Badge>
              <span
                data-testid="bundle-step-headline"
                style={{ fontSize: 13, fontWeight: 600, color: paused ? 'var(--synapse-destructive, #b91c1c)' : 'inherit' }}
              >
                {headline}
              </span>
            </div>
            {actionButton}
          </div>
          <WorkflowProgressCard
            workflowRunId={bundleId}
            steps={steps}
            variant="expanded"
            hideHeader
          />
        </CardContent>
      </Card>
    );
  }

  // ── Single change its plugin presented → that plugin's words and one action.
  const presentation = items.length === 1 ? draftPresentationFor(items[0]!.draftKind) : undefined;
  if (
    presentation &&
    (bundle.status === 'pending' ||
      bundle.status === 'cancelled' ||
      bundle.status === 'rejected' ||
      items[0]!.status === 'applied' ||
      items[0]!.status === 'rejected')
  ) {
    return <PresentedDraftCard bundle={bundle} item={items[0]!} presentation={presentation} onReview={onReview} />;
  }

  // ── Single change → compact one-liner.
  const summary = `1 change · ${STATUS_LABEL[bundle.status]}`;
  return (
    <Card style={{ marginTop: 8 }}>
      <CardContent>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Badge tone={RISK_TONE[bundle.riskBandMax]}>{RISK_LABEL[bundle.riskBandMax]}</Badge>
            <span style={{ fontSize: 13, color: 'var(--synapse-text-muted, #6b7280)' }}>
              {summary}
            </span>
          </div>
          {actionButton}
        </div>
      </CardContent>
    </Card>
  );
};
